import {
    BaseType,
    BuildComponentType,
    CardinalDirection, ColorEnum,
    ElevatorType, HazardousState, HazardType, HeritageProtectionLevel, LevelType,
    Material,
    RiskConstructionType, RoofShape, RoomType,
    UtilityCategory, WindowPaneType
} from "./Enums";
import {BuildingType} from "./BuildingTypeEnums";
import {DoorCategory} from "./protocols";

export const translateOther = (key: string | undefined): string => {
    if (key === undefined) {
        return 'Odefinierat'
    }

    const translations: { [key: string]: string } = {
        roof: "Tak",
        facade: "Fasad",
        foundation: "Grund",
        drainage: "Takavvattning",
        chimney: "Skorsten/murstock",

        asbestos: "Asbest",
        led: "Bly",
        pcb: "PCB",
        arsenic: "Arsenik",
        blueConcrete: "Blåbetong",
        radon: "Radon",

        SUSPECTED: "Misstänkt",
        CONFIRMED: "Bekräftat",
    };

    return translations[key] || key;
};

type TranslateableValue = Material | UtilityCategory | CardinalDirection | BuildComponentType | RiskConstructionType |
    ElevatorType | ColorEnum | HazardType | RoofShape | RoomType | HazardousState | WindowPaneType | LevelType |
    BaseType | BuildingType | HeritageProtectionLevel | DoorCategory
    ;

type ExhaustiveCheck<T extends string | number> = {
    [K in T]: string;
};

export function translate(value: TranslateableValue | undefined | string): string {
    if (value === undefined) {
        return "Odefinierat"
    }

    const translations: ExhaustiveCheck<TranslateableValue> = {
        [Material.Wood]: "Trä",
        [Material.Brick]: "Tegel",
        [Material.SheetMetal]: "Plåt",
        [Material.Render]: "Puts",
        [Material.RenderThick]: "Tjock puts",
        [Material.RenderThin]: "Tunn puts <10mm",
        [Material.Concrete]: "Betong",
        [Material.LightweightConcrete]: "Lättbetong",
        [Material.Granite]: "Granit",
        [Material.Aluminum]: "Aluminium",
        [Material.Pvc]: "PVC",
        [Material.CementBoard]: "Cementbaserad skiva",
        [Material.MetalWorksOnFacade]: "Plåtarbeten på fasad",
        [Material.Leca]: "Leca",
        [Material.Steel]: "Stål",
        [Material.CorrugatedMetal]: "Korrugerad plåt",
        [Material.FiberCement]: "Fibercement",
        [Material.RolledSheetMetal]: "Valsad plåt",
        [Material.RoofingFelt]: "Takpapp",
        [Material.Sedum]: "Sedum",
        [Material.SoilLayer]: "Jordlager",
        [Material.ConcreteCastInPlace]: "Betong platsgjuten",
        [Material.ConcretePrecast]: "Betong prefab",
        [Material.WoodGlulam]: "Limträ",
        [Material.WoodPrefab]: "Trä Prefab",
        [Material.StructuralTimber]: "Konstruktionsvirke",
        [Material.AsbestosCement]: "Eternit",
        [Material.OrganicMaterial]: "Organiskt växtmaterial",
        [Material.SteelDecking]: "Bärande plåt",
        [Material.Stone]: "Sten",
        [Material.Composite]: "Komposit",
        [Material.Gravel]: "Grus",
        [Material.Metal]: "Plåt",
        [Material.Grass]: "Gräs",
        [Material.Plastic]: "Plast",
        [Material.ConcretePainted]: "Målad betong",
        [Material.Drywall]: "Gips",
        [Material.DrywallPainted]: "Målad gips",
        [Material.DrywallWallpaper]: "Tapetserad gips",
        [Material.WoodPanelPainted]: "Målad träpanel",
        [Material.WoodPanelBare]: "Trären panel",
        [Material.RenderPainted]: "Målad puts",
        [Material.CeramicTiles]: "Kakel",
        [Material.FloorTiles]: "Klinker",
        [Material.FloorCarpet]: "Golvmatta textil",
        [Material.FloorLaminate]: "Laminat",
        [Material.FloorVinyl]: "Golvmatta plast",
        [Material.FloorParquet]: "Parkettgolv",
        [Material.AcousticTiles]: "Akustikplattor",
        [Material.DropTiles]: "Nedpendlat undertak",
        [Material.Plywood]: "Plywood",
        [Material.CastIron]: "Gjutjärn",

        [UtilityCategory.Ventilation]: "Ventilation",
        [UtilityCategory.HeatingCooling]: "Värme/kyla",
        [UtilityCategory.Elevator]: "Hiss",
        [UtilityCategory.FireSafety]: "Brandskydd",
        [UtilityCategory.MechanicalMaintenanceRiskConstructions]: "",
        [UtilityCategory.Electrical]: "",
        [UtilityCategory.WaterAndSewage]: "",
        [UtilityCategory.DoorAutomation]: "",

        [CardinalDirection.North]: "Nord",
        [CardinalDirection.NorthEast]: "Nordost",
        [CardinalDirection.East]: "Öst",
        [CardinalDirection.SouthEast]: "Sydöst",
        [CardinalDirection.South]: "Syd",
        [CardinalDirection.SouthWest]: "Sydväst",
        [CardinalDirection.West]: "Väst",
        [CardinalDirection.NorthWest]: "Nordväst",

        [BuildComponentType.FacadeVerticalSiding]: "Stående panel",
        [BuildComponentType.FacadeHorizontalSiding]: "Liggande panel",
        [BuildComponentType.FacadeCavityWall]: "Skalmur",
        [BuildComponentType.FacadeRender]: "Puts >10mm",
        [BuildComponentType.FacadeThinRender]: "Tunnputs <10mm",
        [BuildComponentType.FacadePlinth]: "Sockel",
        [BuildComponentType.FacadeSandwichPanel]: "Sandwichpanel",
        [BuildComponentType.FacadeCorrugatedMetal]: "Korrigerad plåt",

        [BuildComponentType.Geotechnics]: "Geoteknik",

        [BuildComponentType.FoundationGroundSlab]: "Platta på mark",
        [BuildComponentType.FoundationCrawlSpace]: "Krypgrund",
        [BuildComponentType.FoundationSlopingSite]: "Sutteräng-grundläggning",
        [BuildComponentType.FoundationCellar]: "Källargrundläggning",
        [BuildComponentType.FoundationPile]: "Pålgrundläggning",
        [BuildComponentType.FoundationPier]: "Plintgrundläggning",
        [BuildComponentType.FoundationWallOnFooting]: "Grundmur på sula",
        [BuildComponentType.FoundationBlindingLayer]: "Rustbädd",
        [BuildComponentType.FoundationRaisedFloor]: "Plintgrund",

        [BuildComponentType.ChimneyStack]: "Murstock",
        [BuildComponentType.ChimneyFlue]: "Skorstenskanal",
        [BuildComponentType.ChimneyCap]: "Skorstenshuv",

        [BuildComponentType.RoofCorrugatedMetal]: "Korrugerad plåt",
        [BuildComponentType.RoofFiberCement]: "Fibercement",
        [BuildComponentType.RoofRolledSheetMetal]: "Valsad plåt",
        [BuildComponentType.RoofSedum]: "Sedumtak",
        [BuildComponentType.RoofBrick]: "Tegelpannor",
        [BuildComponentType.RoofConcrete]: "Betongpannor",
        [BuildComponentType.RoofGlass]: "Glastak",
        [BuildComponentType.RoofPlastic]: "Plasttak",
        [BuildComponentType.RoofTar]: "Papp",

        [BuildComponentType.DrainageDrainpipe]: "Stuprör",
        [BuildComponentType.DrainageGutter]: "Hängränna",

        [BuildComponentType.GroundDrainagePipe]: "Dräneringsrör",
        [BuildComponentType.GroundDrainageStoneFilled]: "Stenkista",
        [BuildComponentType.GroundDrainageManhole]: "Brunn",
        [BuildComponentType.GroundDrainageCleanout]: "Spolbrunn",
        [BuildComponentType.GroundDrainageMat]: "Dränering",

        [BuildComponentType.StructuralStudWallInterior]: "Regelvägg innervägg",
        [BuildComponentType.StructuralStudWallExterior]: "Regelvägg yttervägg",
        [BuildComponentType.StructuralSandwichInterior]: "Sandwich innervägg",
        [BuildComponentType.StructuralSandwichExterior]: "Sandwich yttervägg",
        [BuildComponentType.StructuralSolidWallInterior]: "Massiv innervägg",
        [BuildComponentType.StructuralSolidWallExterior]: "Massiv yttervägg",
        [BuildComponentType.StructuralPanelsInterior]: "Väggskivor innervägg",
        [BuildComponentType.StructuralPanelsExterior]: "Väggskivor yttervägg",
        [BuildComponentType.StructuralConcreteWall]: "Betongvägg",
        [BuildComponentType.StructuralBrickWall]: "Tegelvägg",
        [BuildComponentType.StructuralMasonryWall]: "Murverk",
        [BuildComponentType.StructuralLightweightConcreteWall]: "Lättbetongvägg",


        [BuildComponentType.StructuralColumnInFacade]: "Pelare i fasad",
        [BuildComponentType.StructuralColumnInteriorWall]: "Pelare i innervägg",
        [BuildComponentType.StructuralButtress]: "Kontrefor",
        [BuildComponentType.StructuralExposedBeam]: "Synliga fria balkar",
        [BuildComponentType.StructuralConcealedBeam]: "Inbyggda balkar",

        [BuildComponentType.StructuralFloorWoodBeam]: "Träbalkar i bjälklag",
        [BuildComponentType.StructuralFloorSteelBeam]: "Stålbalkar i bjälklag",
        [BuildComponentType.StructuralFloorConcreteBeam]: "Betongbalkar i bjälklag",
        [BuildComponentType.StructuralFloorSolidSlab]: "Massivbjälklag",

        [BuildComponentType.StructuralRoofBeam]: "Takbalkar",
        [BuildComponentType.StructuralRoofTruss]: "Takstolar",
        [BuildComponentType.StructuralRoofLightweightConcreteElement]: "Lättbetongelement",
        [BuildComponentType.StructuralRoofCassettes]: "Takkassetter",
        [BuildComponentType.StructuralRoofMetalSheetTrp]: "Takplåt TRP",
        [BuildComponentType.StructuralRoofConcrete]: "Betong",

        [BuildComponentType.OutdoorTerrace]: "Altan",
        [BuildComponentType.OutdoorPavedArea]: "Plattlagd yta",
        [BuildComponentType.OutdoorPavement]: "Gång/trottoar",
        [BuildComponentType.OutdoorWoodenFence]: "Trästaket",
        [BuildComponentType.OutdoorWoodenPlank]: "Träplank",
        [BuildComponentType.OutdoorStoneWall]: "Mur",
        [BuildComponentType.OutdoorGrassArea]: "Gräs",
        [BuildComponentType.OutdoorParkingGravel]: "Grusparkering",
        [BuildComponentType.OutdoorParkingAsphalt]: "Asfaltsparkering",
        [BuildComponentType.OutdoorPavedAreaStone]: "Hårdgjord yta sten",
        [BuildComponentType.OutdoorPavedAreaAsphalt]: "Hårdgjord yta asfalt",
        [BuildComponentType.OutdoorPermanentFurniture]: "Permanenta möbler",
        [BuildComponentType.OutdoorPlayground]: "Lekplats",
        [BuildComponentType.OutdoorSwingSet]: "Gungställning",
        [BuildComponentType.OutdoorSandbox]: "Sandlåda",
        [BuildComponentType.OutdoorSlide]: "Rutschkana",
        [BuildComponentType.OutdoorOther]: "Övrigt",

        [BuildComponentType.InteriorWall]: "Innervägg",
        [BuildComponentType.InteriorCeiling]: "Innertak",
        [BuildComponentType.InteriorFloor]: "Innergolv",

        // Door types
        [BuildComponentType.DoorWood]: "Trädörr",
        [BuildComponentType.DoorAluminium]: "Aluminiumdörr",
        [BuildComponentType.DoorPvc]: "PVC-dörr",
        [BuildComponentType.DoorGlass]: "Glasdörr",
        
        // Window types
        [BuildComponentType.WindowWood]: "Träfönster",
        [BuildComponentType.WindowAluminium]: "Aluminiumfönster",
        [BuildComponentType.WindowPvc]: "PVC-fönster",
        [BuildComponentType.WindowGlass]: "Glasfönster",

        [BuildComponentType.SheetMetalPainted]: "Plåtarbete - målad/lackad",
        [BuildComponentType.SheetMetalAluZinc]: "Plåtarbete - alu zink",
        [BuildComponentType.SheetMetalCopper]: "Plåtarbete - koppar",
        [BuildComponentType.SheetMetalGalvanized]: "Plåtarbete - galvad",
        [BuildComponentType.SheetMetalUntreatedSteel]: "Plåtarbete - obehandlat stål",
        [BuildComponentType.SheetMetalStainlessSteel]: "Plåtarbete - rostfritt stål",

        [BuildComponentType.UtilityGeothermalHeatPumpEarth]: "Jordvärmepump",
        [BuildComponentType.UtilityGeothermalHeatPumpBedrock]: "Bergvärmepump",
        [BuildComponentType.UtilityElectricBoiler]: "Elpanna",
        [BuildComponentType.UtilityPelletBoiler]: "Pelletsvärmepanna",
        [BuildComponentType.UtilityWoodBoiler]: "Vedpanna",
        [BuildComponentType.UtilityAirToAirHeatPump]: "Luftvärmepump (luft-luft)",
        [BuildComponentType.UtilityAirToWaterHeatPump]: "Luftvärmepump (luft/vatten)",
        [BuildComponentType.UtilityAirConditioning]: "AC",
        [BuildComponentType.UtilityDistrictCooling]: "Fjärrkyla",
        [BuildComponentType.UtilityPoolHeating]: "Poolvärme",

        [BuildComponentType.UtilitySVentilation]: "S-ventilation: Självdragsventilation",
        [BuildComponentType.UtilityFVentilation]: "F-ventilation: Fläktventilation frånluft",
        [BuildComponentType.UtilityFtVentilation]: "FT-ventilation: Fläktventilation till- och frånluft",
        [BuildComponentType.UtilityFxVentilation]: "FX-ventilation: F-ventilation med värmeåtervinning",
        [BuildComponentType.UtilityFtxVentilation]: "FTX-ventilation: FT-ventilation med värmeåtervinnning",

        [BuildComponentType.UtilityPassengerElevator]: "Personhiss",
        [BuildComponentType.UtilityFreightElevator]: "Varu/transport-hiss",

        [BuildComponentType.UtilitySmokeDetector]: "Brandvarnare",
        [BuildComponentType.UtilityFireAlarm]: "Brandlarm",
        [BuildComponentType.UtilityFireBlanket]: "Brandfilt",
        [BuildComponentType.UtilityFireExtinguisher]: "Brandsläckare",
        [BuildComponentType.UtilitySprinkler]: "Sprinkler",
        [BuildComponentType.UtilityFireControlPanel]: "Brandcentral",

        [BuildComponentType.UtilityMechanicalDehumidification]: "Mekanisk avfuktning",
        [BuildComponentType.UtilityRadonExtractor]: "Radonsug",
        [BuildComponentType.UtilityVentilatedFloorUnit]: "Ventilerade golv - aggregat",
        [BuildComponentType.UtilitySorptionDehumidification]: "Sorptionsavfuktning",
        [BuildComponentType.UtilitySafetyGuardSystem]: "Trygghetsvakt",

        [BuildComponentType.UtilityElectricMainSwitchboard]: "Huvudcentral",
        [BuildComponentType.UtilityElectricSubSwitchboard]: "Undercentral",

        [BuildComponentType.UtilityToiletWater]: "Vattentoalett",
        [BuildComponentType.UtilityToiletClosed]: "Bajamaja",
        [BuildComponentType.UtilityToiletSolar]: "Solcellstoalett",
        [BuildComponentType.UtilityToiletCompost]: "Kompost-toalett",
        [BuildComponentType.UtilityToiletWasteBarrels]: "Latrintunnor",

        [BuildComponentType.UtilityBathroomSink]: "Handfat",
        [BuildComponentType.UtilityShower]: "Dusch",
        [BuildComponentType.UtilityPorcelainToilet]: "Toalett",
        [BuildComponentType.UtilityBathtub]: "Badkar",
        [BuildComponentType.UtilityKitchenSink]: "Diskbänk",
        [BuildComponentType.UtilityRefrigerator]: "Kylskåp",
        [BuildComponentType.UtilityFreezer]: "Frys",
        [BuildComponentType.UtilityCombinedRefrigeratorFreezer]: "Kombinerad kyl/frys",
        [BuildComponentType.UtilityOven]: "Ugn",
        [BuildComponentType.UtilityStove]: "Spis",
        [BuildComponentType.UtilityDishwasher]: "Diskmaskin",
        [BuildComponentType.UtilityMicrowave]: "Mikrovågsugn",
        [BuildComponentType.UtilityKitchenExhaustfan]: "Köksfläkt",
        [BuildComponentType.UtilityWashingMachine]: "Tvättmaskin",
        [BuildComponentType.UtilityDryer]: "Torktumlare",
        [BuildComponentType.UtilityDryingCabinet]: "Torkskåp",
        [BuildComponentType.UtilityMangle]: "Mangel",
        [BuildComponentType.UtilityDrilledWell]: "Borrad brunn",
        [BuildComponentType.UtilityWellPump]: "Brunnspump",

        [BuildComponentType.DoorDefault]: "Dörr",
        [BuildComponentType.WindowDefault]: "Fönster",


        [RiskConstructionType.RiskCrawlSpace]: "Krypgrund",
        [RiskConstructionType.RiskSingleStageSealedRender]: "Enstegstätad putsfasad (utan luftspalt)",
        [RiskConstructionType.RiskFloatingFloor]: "Flytande golv",
        [RiskConstructionType.RiskUninsulatedSlabOnGround]: "Oisolerad platta på mark",
        [RiskConstructionType.RiskExternallyVentilatedAttic]: "Uteluftsventilerad vind",
        [RiskConstructionType.RiskCompactRoofNoAirGap]: "Kompakttak (utan luftspalt)",
        [RiskConstructionType.RiskInternalDrainage]: "Invändig avvattning",
        [RiskConstructionType.RiskLowSlopeRoof]: "Låglutande tak",
        [RiskConstructionType.RiskBuiltInWaterConnections]: "Dolda/inbyggda vattenkopplingar",
        [RiskConstructionType.RiskExternalWallNoVaporBarrier]: "Yttervägg utan diffusionsspärr",
        [RiskConstructionType.RiskUngroundedElectricalSystem]: "Ojordat elsystem",
        [RiskConstructionType.RiskCablesThroughExternalWallRoof]: "Kabelgenomföringar genom yttervägg och yttertak",

        [ElevatorType.TractionElevator]: "Linhiss",
        [ElevatorType.Stairlift]: "Trapphiss",
        [ElevatorType.HydraulicElevator]: "Hydraulisk hiss",

        [ColorEnum.ColorWhite]: "Vit",
        [ColorEnum.ColorBlack]: "Svart",
        [ColorEnum.ColorLightGrey]: "Ljusgrå",
        [ColorEnum.ColorDarkGrey]: "Mörkgrå",
        [ColorEnum.ColorBeige]: "Beige",
        [ColorEnum.ColorYellow]: "Gul",
        [ColorEnum.ColorLightBlue]: "Ljusblå",
        [ColorEnum.ColorDarkBlue]: "Mörkblå",
        [ColorEnum.ColorLightGreen]: "Ljusgrön",
        [ColorEnum.ColorDarkGreen]: "Mörkgrön",
        [ColorEnum.ColorRed]: "Röd",
        [ColorEnum.ColorFaluRed]: "Faluröd",
        [ColorEnum.ColorBrown]: "Brun",
        [ColorEnum.ColorTerracotta]: "Terrakotta",
        [ColorEnum.ColorPink]: "Rosa",
        [ColorEnum.ColorPurple]: "Lila",
        [ColorEnum.ColorOrange]: "Orange",
        [ColorEnum.ColorTurquoise]: "Turkos",

        [HazardType.HazardAsbestos]: "Asbest",
        [HazardType.HazardLed]: "Bly",
        [HazardType.HazardPcb]: "PCB",
        [HazardType.HazardArsenic]: "Arsenik",
        [HazardType.HazardBlueConcrete]: "Blåbetong",
        [HazardType.HazardRadon]: "Radon",

        [RoofShape.RoofShapeGable]: "Sadeltak",
        [RoofShape.RoofShapeMansard]: "Mansardtak",
        [RoofShape.RoofShapeHip]: "Valmat tak",
        [RoofShape.RoofShapeShed]: "Pulpettak",
        [RoofShape.RoofShapeInvertedShed]: "Motfallstak",
        [RoofShape.RoofShapeCylindrical]: "Cylindriskt tak",
        [RoofShape.RoofShapeLowSlope]: "Flackt tak",
        [RoofShape.RoofShapeFlat]: "Platt tak",

        [RoomType.RoomGeneral]: "Allmänt",
        [RoomType.RoomKitchen]: "Kök",
        [RoomType.RoomAssembly]: "Matsal/samlingshall",
        [RoomType.RoomOffice]: "Kontor",
        [RoomType.RoomBathroom]: "Badrum/toalett",
        [RoomType.RoomStorage]: "Förråd/förvaring",
        [RoomType.RoomUtility]: "Teknikrum",
        [RoomType.RoomEntryway]: "Hall",
        [RoomType.RoomHallway]: "Korridor",
        [RoomType.RoomChanging]: "Omklädningsrum",
        [RoomType.RoomWork]: "Arbetsrum",
        [RoomType.RoomConference]: "Konferens",
        [RoomType.RoomLecture]: "Föreläsning",
        [RoomType.RoomExhibit]: "Utställning",
        [RoomType.RoomWarmAttic]: "Varmvind",
        [RoomType.RoomColdAttic]: "Kallvind",
        [RoomType.RoomLaundry]: "Tvättstuga",
        [RoomType.RoomStairwell]: "Trapphus",
        
        [RoomType.RoomBedroom]: "Sovrum",
        [RoomType.RoomLivingRoom]: "Vardagsrum",
        [RoomType.RoomDiningRoom]: "Matsal",
        [RoomType.RoomServer]: "Serverrum",
        [RoomType.RoomCleaning]: "Städrum",
        [RoomType.RoomTrash]: "Soprum",
        [RoomType.RoomBikeStorage]: "Cykelförråd",
        [RoomType.RoomReception]: "Reception",
        [RoomType.RoomCafeteria]: "Cafeteria",
        [RoomType.RoomGym]: "Gym",
        [RoomType.RoomArchive]: "Arkiv",
        [RoomType.RoomGarage]: "Garage",
        [RoomType.RoomMedical]: "Behandlingsrum",
        [RoomType.RoomLibrary]: "Bibliotek",
        [RoomType.RoomWorkshop]: "Verkstad",

        [HazardousState.Suspected]: "Misstänkt",
        [HazardousState.Confirmed]: "Bekräftad",

        [WindowPaneType.OnePane]: "Enkelglas",
        [WindowPaneType.TwoPane]: "2-glas",
        [WindowPaneType.ThreePane]: "3-glas",

        [DoorCategory.interior_door]: "Innerdörr",
        [DoorCategory.exterior_door]: "Ytterdörr",

        [LevelType.Basement]: "Källare",
        [LevelType.GroundFloor]: "Bottenvåning",
        [LevelType.NthFloor]: "Våning",
        [LevelType.Attic]: "Vind",

        [BaseType.Ceiling]: "Innertak",
        [BaseType.Chimney]: "Skorsten/murstock",
        [BaseType.RoofDrainage]: "Takavvattning",
        [BaseType.GroundDrainage]: "Dränering",
        [BaseType.Facade]: "Fasad",
        [BaseType.Floor]: "Golv",
        [BaseType.Foundation]: "Grund",
        [BaseType.GeoTechnics]: "Geoteknik",
        [BaseType.InteriorWall]: "Innervägg",
        [BaseType.Outdoor]: "Utomhusobjekt",
        [BaseType.Roof]: "Tak",
        [BaseType.Structural]: "Bärande konstruktion",
        [BaseType.Wall]: "Vägg",
        [BaseType.Window]: "Fönster",
        [BaseType.Utility]: "Fastighetsteknik",
        [BaseType.Door]: "Dörr",
        [BaseType.SheetMetal]: "Kompletterande plåtarbeten",

        [BuildingType.standard]: "Byggnad",
        [BuildingType.complement]: "Komplementsbyggnad",
        [BuildingType.toiletArchipelago]: "Skärgårdstoalett",
        [BuildingType.toiletReserve]: "Reservatstoalett",
        [BuildingType.other]: "Annat byggnadsverk",

        [HeritageProtectionLevel.Byggnadsminne]: "Byggnadsminne",
        [HeritageProtectionLevel.KMarkt]: "K-märkt byggnad",
        [HeritageProtectionLevel.QMarkt]: "Q-märkt byggnad",
        [HeritageProtectionLevel.KulturhistorisktVardefull]: "Kulturhistoriskt värdefull byggnad",
        [HeritageProtectionLevel.Riksintresse]: "Riksintresse för kulturmiljövård",

    } as const;

    const translationKey = value as keyof typeof translations;
    return translations[translationKey] ?? translationKey;
}
